import React, { useState, type JSX } from 'react';
import { 
  FaPizzaSlice, FaLeaf, FaFish, FaDrumstickBite, FaGlassCheers, 
  FaMusic, FaUmbrellaBeach, FaCrown, FaWifi, FaPaw, FaCar, 
  FaChild, FaWheelchair, FaUtensils, 
  FaMoneyBill,
  FaCocktail
} from 'react-icons/fa';
import { GiFrance, GiNoodles, GiTacos, GiSushis, GiGreekTemple, GiMeal } from 'react-icons/gi';
import { MdFamilyRestroom, MdBusinessCenter, MdOutdoorGrill, MdOutlineFilterVintage, MdDinnerDining } from 'react-icons/md';
import { IoFastFood } from 'react-icons/io5';
import type { RestaurantFormData } from '../restaurant.types';
import { FaX } from 'react-icons/fa6';
import { Cookie, Gift, PersonStanding, Truck } from 'lucide-react';

interface CuisineStepProps {
  formData: RestaurantFormData;
  updateFormData: (data: Partial<RestaurantFormData>) => void;
  isActive: boolean;
  setDisableNext?: (disabled: boolean) => void;
}

const CuisineStep: React.FC<CuisineStepProps> = ({
  formData,
  updateFormData,
  isActive,
}) => {
  const [customCuisine, setCustomCuisine] = useState('');
  const [customFood, setCustomFood] = useState('');
  const [customExperience, setCustomExperience] = useState('');
  const [customFeature, setCustomFeature] = useState('');

  const cuisineOptions = [
    { id: 'italian', label: 'Italian', icon: <FaPizzaSlice /> },
    { id: 'french', label: 'French', icon: <GiFrance /> },
    { id: 'mexican', label: 'Mexican', icon: <GiTacos /> },
    { id: 'chinese', label: 'Chinese', icon: <GiNoodles /> },
    { id: 'indian', label: 'Indian', icon: <FaUtensils /> },
    { id: 'japanese', label: 'Japanese', icon: <GiSushis /> },
    { id: 'thai', label: 'Thai', icon: <GiNoodles /> },
    { id: 'mediterranean', label: 'Mediterranean', icon: <GiGreekTemple /> },
  ];

  const foodTypeOptions = [
    { id: 'vegetarian', label: 'Vegetarian', icon: <FaLeaf /> },
    { id: 'seafood', label: 'Seafood', icon: <FaFish /> },
    { id: 'steakhouse', label: 'Steakhouse', icon: <FaDrumstickBite /> },
    { id: 'vegan', label: 'Vegan', icon: <FaLeaf /> },
    { id: 'Cocktails', label: 'Cocktails', icon: <FaCocktail /> },
    { id: 'gluten-free', label: 'Gluten-Free', icon: <FaLeaf /> },
    { id: 'halal', label: 'Halal', icon: <GiMeal /> },
    { id: 'fast-food', label: 'Fast Food', icon: <IoFastFood /> },
  ];

  const experienceOptions = [
    { id: 'fine-dining', label: 'Fine Dining', icon: <FaCrown /> },
    { id: 'solo-dining', label: 'Solo Dining', icon: <PersonStanding /> },
    { id: 'family-friendly', label: 'Family Friendly', icon: <MdFamilyRestroom /> },
    { id: 'business', label: 'Business', icon: <MdBusinessCenter /> },
    { id: 'romantic', label: 'Romantic', icon: <FaGlassCheers /> },
    { id: 'dinner', label: 'Dinner', icon: <MdDinnerDining /> },
    { id: 'brunch', label: 'Brunch', icon: <Cookie /> },
    { id: 'outdoor', label: 'Outdoor Seating', icon: <MdOutdoorGrill /> },
    { id: 'live-music', label: 'Live Music', icon: <FaMusic /> },
    { id: 'waterfront', label: 'Waterfront', icon: <FaUmbrellaBeach /> },
  ];

  const specialFeaturesOptions = [
    { id: 'live-music', label: 'Live Music', icon: <FaMusic /> },
    { id: 'wifi', label: 'Wi-Fi', icon: <FaWifi /> },
    { id: 'pet-friendly', label: 'Pet Friendly', icon: <FaPaw /> },
    { id: 'Parking', label: 'Free Parking', icon: <FaCar /> },
    { id: 'Delivery', label: 'Delivery', icon: <Truck /> },
    { id: 'kid-friendly', label: 'Kid Friendly', icon: <FaChild /> },
    { id: 'halal', label: 'Halal', icon: <GiMeal /> },
    { id: 'wheel-chair', label: 'Wheel-Chair Accessible', icon: <FaWheelchair /> },
    { id: 'Takeaway', label: 'Takeaways', icon: <Gift /> },
    { id: 'Mobile Payments', label: 'Mobile Payments', icon: <FaMoneyBill /> },
  ];

  const toggleSelection = (category: 'cuisine' | 'typeOfFood' | 'experience' | 'tags', value: string) => {
    const currentArray = formData[category];
    const newArray = currentArray.includes(value)
      ? currentArray.filter(item => item !== value)
      : [...currentArray, value];
    
    updateFormData({ [category]: newArray });
  };

  const addCustom = (category: 'cuisine' | 'typeOfFood' | 'experience' | 'tags', value: string, setter: React.Dispatch<React.SetStateAction<string>>) => {
    if (value.trim() && !formData[category].includes(value.trim())) {
      const newArray = [...formData[category], value.trim()];
      updateFormData({ [category]: newArray });
      setter('');
    }
  };
  const defaultIcons = {
    cuisine: <FaPizzaSlice />,
    typeOfFood: <FaUtensils />,
    experience: <FaGlassCheers />,
    tags: <MdOutlineFilterVintage /> // or whatever fits
  };

  const renderTagSection = (
    title: string,
    description: string,
    options: { id: string; label: string; icon: JSX.Element }[],
    category: 'cuisine' | 'typeOfFood' | 'experience' | 'tags',
    customValue: string,
    setCustomValue: React.Dispatch<React.SetStateAction<string>>,
    required?: boolean
  ) => {
    // 👇 Merge static + custom values into one strongly typed array
    const mergedOptions: { id: string; label: string; icon: JSX.Element }[] = [
      ...options,
      ...formData[category]
        .filter(value => !options.some(opt => opt.id === value))
        .map(value => ({
          id: value,
          label: value,
          icon: defaultIcons[category] 
        }))
    ];

    return (
      <div className={`space-y-4 ${isActive ? 'fade-in' : 'opacity-0'}`}>
        <div>
          <h3 className="text-lg font-semibold text-[#111827]">
            {title} {required && '*'}
          </h3>
          <p className="text-sm text-[#5E503F]">{description}</p>
        </div>

        {/* Options Grid */}
        <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
          {mergedOptions.map((option, index) => {
            const isSelected = formData[category].includes(option.id);
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => toggleSelection(category, option.id)}
                className={`
                  flex items-center gap-2 p-3 rounded-lg border-2 transition-all duration-200 text-left
                  ${isSelected 
                    ? 'border-[#E6B74C] bg-[#FFF3D6] shadow-sm' 
                    : 'border-[#EDEDED] bg-white hover:border-[#FFD166] hover:bg-[#FFF8E7]'
                  }
                `}
              >
                <span className={`text-lg ${isSelected ? 'text-[#FFD166]' : 'text-[#5E503F]'}`}>
                  {option.icon}
                </span>
                <span className="font-medium text-[#111827] text-sm block truncate">
                  {option.label}
                </span>
                {isSelected && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSelection(category, option.id);
                    }}
                    className="w-5 h-5 rounded-full bg-[#FFD166] text-[#111827] flex items-center justify-center text-xs hover:bg-[#E1AD01] transition-colors ml-1"
                  >
                    <FaX size={8} />
                  </button>
                )}
              </button>
            );
          })}
        </div>

        {/* Custom Input */}
        <div className="flex gap-2">
          <input
            type="text"
            value={customValue}
            onChange={(e) => setCustomValue(e.target.value)}
            placeholder={`Add custom ${title.toLowerCase()}`}
            className="flex-1 px-3 py-2 border border-[#FFDDA6] rounded-lg focus:ring-2 focus:ring-[#FFD166] focus:border-transparent text-sm"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addCustom(category, customValue, setCustomValue);
              }
            }}
          />
          <button
            type="button"
            onClick={() => addCustom(category, customValue, setCustomValue)}
            className="px-4 py-2 bg-[#FFD166] text-[#111827] font-semibold rounded-lg hover:bg-[#E1AD01] transition-colors text-sm"
          >
            Add
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-6">
        {renderTagSection(
          'Cuisine Types',
          'Select from popular options or add your own',
          cuisineOptions,
          'cuisine',
          customCuisine,
          setCustomCuisine,
          true
        )}

        <div className="pt-4 border-t border-[#FFDDA6]">
          {renderTagSection(
            'Food Types',
            'Select dietary preferences and food styles',
            foodTypeOptions,
            'typeOfFood',
            customFood,
            setCustomFood
          )}
        </div>

        <div className="pt-4 border-t border-[#FFDDA6]">
          {renderTagSection(
            'Dining Experiences',
            'Select the type of dining experience you offer',
            experienceOptions,
            'experience',
            customExperience,
            setCustomExperience
          )}
        </div>

        <div className="pt-4 border-t border-[#FFDDA6]">
          {renderTagSection(
            'Special Features/Services',
            'What makes your place unique or convenient. Tell guests what your place has to offer.',
            specialFeaturesOptions,
            'tags',
            customFeature,
            setCustomFeature
          )}
        </div>
      </div>
    </div>
  );
};

export default CuisineStep;